/**
 * The operation log: keep every edit, so any moment can be read back.
 *
 * WHAT IT CLOSES. The audit trail records every save; the TEXT is only kept every five minutes,
 * because storing a copy of a procedure per keystroke would be absurd. So "who changed this and
 * when" is answerable for every event and "what did it say at 10:03" only to the nearest snapshot.
 * Google Docs has no such gap, and not because it stores more copies -- it stores the OPERATION
 * STREAM and replays it, and its version list is that stream grouped for reading rather than
 * sampled. We already produce the same stream: the editor is Yjs, every edit is a CRDT update, and
 * those updates already travel between browsers. Until now we dropped them.
 *
 * REPLAY HAPPENS HERE, NOT ON THE SERVER. The browser already has Yjs loaded and is the only place
 * a ProseMirror document can be rendered; the lambda stores and serves opaque bytes. That keeps one
 * CRDT implementation in the system instead of two that have to agree about a document forever.
 *
 * FLUSHED ON A TIMER, AND LOSING A FLUSH IS SURVIVABLE. The body is still saved the way it always
 * was and the digest is still what gets signed; this log makes the history finer and is not the
 * record of record. That is what makes it safe to write from a browser.
 */
import * as Y from "yjs";
import { yDocToProsemirrorJSON } from "y-prosemirror";

import { encode, decode } from "../../util/YMqttProvider";

/** How long a flush waits for more typing. Long enough to batch, short enough to survive a tab. */
const FLUSH_MS = 5000;

/** The fragment TipTap's Collaboration extension writes into when no field is configured. */
const FRAGMENT = "default";

export type UpdateLog = {
    /** Write the whole document state as one update, so replay never starts at the beginning. */
    checkpoint: () => void;
    /** Send whatever is pending, now. */
    flush: () => void;
    stop: () => void;
};

/**
 * Start recording this document's edits.
 *
 * ONLY WHILE EDITABLE. A read-only window produces no updates of its own, and appending is gated
 * server-side anyway -- starting the log there would be a refusal every five seconds.
 */
export function startUpdateLog(
    { doc, send, onProblem }:
    {
        doc: Y.Doc;
        /** THE PAGE OWNS THE TRANSPORT, as it does for figures: the editor edits. */
        send: (updates: string[], checkpoint?: string) => Promise<any>;
        onProblem?: (reason: string) => void;
    },
): UpdateLog {
    let pending: Uint8Array[] = [];
    let timer: any = null;
    let stopped = false;

    const flushNow = async (checkpoint?: Uint8Array) => {
        if (stopped) return;
        const batch = pending;
        pending = [];
        if (!batch.length && !checkpoint) return;
        try {
            await send(batch.map(encode), checkpoint ? encode(checkpoint) : undefined);
        } catch (error: any) {
            // PUT BACK, NOT DROPPED: a refused flush is usually a lost connection, and the next
            // one carries both. The log is allowed to be late; it is not allowed to have holes.
            pending = batch.concat(pending);
            onProblem?.(error?.response?.data?.Cause || "Edits could not be recorded.");
        }
    };

    const onUpdate = (update: Uint8Array, origin: unknown) => {
        // NOT THE ONES THAT ARRIVED FROM A PEER. The window that typed them records them, so a
        // two-person session stores each edit once rather than once per browser.
        if (origin === "remote") return;
        pending.push(update);
        if (!timer) timer = setTimeout(() => { timer = null; void flushNow(); }, FLUSH_MS);
    };

    doc.on("update", onUpdate);

    // THE TAB CLOSING IS THE COMMONEST WAY TO LOSE A FLUSH, and `visibilitychange` is the event
    // that actually fires on mobile and on a closed laptop -- `beforeunload` does not.
    const onHidden = () => { if (document.visibilityState === "hidden") void flushNow(); };
    document.addEventListener("visibilitychange", onHidden);

    return {
        checkpoint: () => void flushNow(Y.encodeStateAsUpdate(doc)),
        flush: () => void flushNow(),
        stop: () => {
            stopped = true;
            doc.off("update", onUpdate);
            document.removeEventListener("visibilitychange", onHidden);
            if (timer) clearTimeout(timer);
        },
    };
}

/**
 * What the document looked like at a moment, rebuilt from the log.
 *
 * RETURNS NULL WHEN IT CANNOT BE SURE. A partial replay renders a document that never existed,
 * which is worse than showing nothing -- so an incomplete answer, or one with no updates at all,
 * is refused rather than approximated.
 */
export async function replayAt(axios: any, documentUSID: string, at?: string): Promise<any | null> {
    const response = await axios.get(
        `/t11/v1/document_updates?USID=${encodeURIComponent(documentUSID)}`
        + (at ? `&Until=${encodeURIComponent(at)}` : ""));
    const data = response?.data;
    if (!data?.Count || !data?.Complete) return null;

    const doc = new Y.Doc();
    // APPLIED IN THE ORDER THE SERVER GAVE THEM. Yjs does not actually require the order -- that is
    // the point of a CRDT -- but the checkpoint has to land before the increments that follow it,
    // and "in order" is the only rule that is true of both kinds.
    data.Updates.forEach((one: any) => Y.applyUpdate(doc, decode(one.Payload)));
    return yDocToProsemirrorJSON(doc, FRAGMENT);
}
